import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Plus, Edit, Trash2, AlertCircle } from 'lucide-react';
import { 
  useGetMappingsForInputQuery, 
  useCreateCustomerCodeMappingMutation,
  useUpdateCustomerCodeMappingMutation,
  useDeleteCustomerCodeMappingMutation,
  CustomerCodeMapping 
} from '../../store/is/customerCodeMappingApi';
import { CustomerCodeMappingForm } from './CustomerCodeMappingForm';

interface CustomerCodeMappingSectionProps {
  inputId: string;
  inputName: string;
  inputType: string;
  isAdmin: boolean;
}

export const CustomerCodeMappingSection: React.FC<CustomerCodeMappingSectionProps> = ({
  inputId,
  inputName,
  inputType,
  isAdmin
}) => {
  const [showForm, setShowForm] = useState(false);
  const [editingMapping, setEditingMapping] = useState<CustomerCodeMapping | null>(null);

  const getMappingId = (m: any): string => m?.mapping_id || m?._id || '';

  const { data: mappings = [], isLoading, error, refetch } = useGetMappingsForInputQuery(inputId);
  const [createMapping] = useCreateCustomerCodeMappingMutation();
  const [updateMapping] = useUpdateCustomerCodeMappingMutation();
  const [deleteMapping] = useDeleteCustomerCodeMappingMutation();


  console.log('mappings', JSON.stringify(mappings, null, 2));


  const handleAddMapping = () => {
    setEditingMapping(null);
    setShowForm(true);
  };

  const handleEditMapping = (mapping: CustomerCodeMapping) => {
    const id = getMappingId(mapping);
    console.log('Edit mapping clicked, id:', id);
    setEditingMapping(mapping);
    setShowForm(true);
  };

  const handleDeleteMapping = async (mappingId: string) => {
    if (window.confirm('Are you sure you want to delete this customer code mapping?')) {
      try {
        console.log('Deleting mapping:', mappingId);
        await deleteMapping(mappingId).unwrap();
        console.log('Delete successful');
        await refetch();
      } catch (error) {
        console.error('Failed to delete mapping:', error);
      }
    }
  };

  const handleFormSubmit = async (mappingData: Partial<CustomerCodeMapping>) => {
    try {
      if (editingMapping) {
        const id = getMappingId(editingMapping);
        if (!id) {
          console.error('Missing mapping id for update');
          return;
        }
        console.log('Updating mapping:', { id, body: mappingData });
        await updateMapping({ id, body: mappingData }).unwrap();
        console.log('Update successful');
        await refetch();
      } else {
        console.log('Creating mapping:', { ...mappingData, input_id: inputId });
        await createMapping({
          ...mappingData,
          input_id: inputId
        }).unwrap();
        console.log('Create successful');
        await refetch();
      }
      setShowForm(false);
      setEditingMapping(null);
    } catch (error) {
      console.error('Failed to save mapping:', error);
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingMapping(null);
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setShowForm(false);
      setEditingMapping(null);
    }
  };

  // no status/mapping type badges anymore

  if (!isAdmin) {
    return null;
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Customer Code Mappings</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4">Loading mappings...</div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Customer Code Mappings</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-red-600">
            <AlertCircle className="h-4 w-4" />
            <span>Failed to load customer code mappings</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Customer Code Mappings</CardTitle>
          <Button onClick={handleAddMapping} size="sm">
            <Plus className="h-4 w-4 mr-2" />
            Add Mapping
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {mappings.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>No customer code mappings found for this input.</p>
            <p className="text-sm">Click "Add Mapping" to create one.</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Customer Org</TableHead>
                <TableHead>Input Code</TableHead>
                <TableHead>Inhibitor Code</TableHead>
                <TableHead>Crop Code</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {mappings.map((mapping) => (
                <TableRow key={getMappingId(mapping)}>
                  <TableCell className="font-medium">
                    {mapping.customer_org_id}
                  </TableCell>
                  <TableCell>{mapping.codes?.input_code || '-'}</TableCell>
                  <TableCell>{mapping.codes?.inhibitor || '-'}</TableCell>
                  <TableCell>{mapping.codes?.crop || '-'}</TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditMapping(mapping)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteMapping(getMappingId(mapping))}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <CustomerCodeMappingForm
          inputId={inputId}
          inputName={inputName}
          inputType={inputType}
          initialData={editingMapping}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
          open={showForm}
          onOpenChange={handleOpenChange}
        />
      </CardContent>
    </Card>
  );
};
